import React, {
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { useLocation, useSearchParams } from 'react-router';
import type { CapiNews, FeatureListItem } from 'types/capi/news';

import ChevronIcon from '~/assets/icons/Chevron';
import Image from '~/components/Image';
import Link from '~/components/Link';
import ArticleCardInteractionEvent from '~/events/ArticleCardInteractionEvent';
import useArticleCardImpression from '~/hooks/useArticleCardImpression';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import Pagination from '~/routes/plps/components/Products/Pagination';
import { formatArticleListItemData } from '~/utils/dates';
import type { Region } from '~/utils/region';
import { appendTrailingSlash } from '~/utils/trailingSlash';

export const FEED_MODULE_TYPES = {
  authorArchive: 'author-archive-list',
  featured: 'news-feed-featured',
  list: 'news-feed-list',
  search: 'news-search-results'
};

function FeedArticleLink({
  article,
  children,
  className,
  moduleType,
  position
}: {
  article: FeatureListItem;
  children: ReactNode;
  className?: string;
  moduleType: string;
  position: number;
}) {
  const { isBot } = useRootData();
  const user = useUser();
  const impressionRef = useArticleCardImpression<HTMLAnchorElement>({
    articleId: article.id,
    moduleType,
    position
  });

  return (
    <Link
      className={className}
      onClick={() => {
        new ArticleCardInteractionEvent({
          articleId: article.id,
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          moduleType,
          position
        }).send();
      }}
      ref={impressionRef}
      to={article.path}
    >
      {children}
    </Link>
  );
}

type NewsSection = { label: string; nonGendered?: boolean; value: string };
type NewsGender = { label: string; value: string };

export const NEWS_SECTIONS: NewsSection[] = [
  { label: 'All news', value: '' },
  { label: 'Footwear', value: 'footwear' },
  { label: 'Clothing', value: 'clothing' },
  { label: 'Size guides', nonGendered: true, value: 'size-guides' },
  { label: 'Lifestyle', nonGendered: true, value: 'lifestyle' },
  { label: 'Features', nonGendered: true, value: 'features' }
];

export const NEWS_GENDERS: NewsGender[] = [
  { label: 'All', value: '' },
  { label: 'Men', value: 'mens' },
  { label: 'Women', value: 'womens' }
];

function buildNewsPath(genderValue: string, sectionValue: string) {
  const segments = [genderValue, 'news', sectionValue].filter(Boolean);

  return `/${segments.join('/')}/`;
}

function findSection(segments: string[]) {
  return (
    NEWS_SECTIONS.find(
      (section) => section.value && segments.includes(section.value)
    ) ?? NEWS_SECTIONS[0]!
  );
}

function findGender(segments: string[]) {
  return (
    NEWS_GENDERS.find(
      (gender) => gender.value && segments.includes(gender.value)
    ) ?? NEWS_GENDERS[0]!
  );
}

export default function NewsFeedView() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const region = useRegion();
  const { _schemas, features, pagination, title } = usePageData<CapiNews>();

  const searchTerm = searchParams.get('q')?.trim();
  const isSearch = Boolean(searchTerm);

  const segments = appendTrailingSlash(pathname).split('/').filter(Boolean);
  const section = findSection(segments);
  const gender = findGender(segments);
  const isIndex = !section.value && !gender.value;

  const crumbs: Crumb[] = isIndex
    ? [{ label: 'Home', to: '/' }, { label: 'News' }]
    : [
        { label: 'Home', to: '/' },
        { label: 'News', to: '/news/' },
        { label: title }
      ];

  return (
    <div className="content-width mb-12 mt-6 flex flex-col gap-12 md:mt-12">
      {_schemas ? <StructuredData schemas={_schemas} /> : null}
      <div className="flex w-full flex-col items-stretch gap-6 md:gap-12">
        <div className="flex flex-col gap-2">
          <NewsBreadcrumbs crumbs={crumbs} />
          <Title>{title}</Title>
        </div>
        {isSearch ? null : <NewsFilters gender={gender} section={section} />}
      </div>
      {isSearch ? (
        features.length ? (
          <ArticleFeedList
            features={features}
            moduleType={FEED_MODULE_TYPES.search}
            region={region}
          />
        ) : (
          <NewsSearchEmpty searchTerm={searchTerm!} />
        )
      ) : (
        <>
          <FeaturedArticles features={features.slice(0, 3)} />
          <ArticleFeedList
            features={features.slice(3)}
            moduleType={FEED_MODULE_TYPES.list}
            region={region}
          />
        </>
      )}
      {pagination.pages ? <Pagination pagination={pagination} /> : null}
    </div>
  );
}

function NewsSearchEmpty({ searchTerm }: { searchTerm: string }) {
  return (
    <p className="text-center text-light-grey">
      {`No articles found for “${searchTerm}”.`}
    </p>
  );
}

export function StructuredData({ schemas }: { schemas: object[] }) {
  useStructuredData(schemas);

  return null;
}

type Crumb = { label: string; to?: string };

export function NewsBreadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <div className="text-caption-grey flex gap-3 self-center">
      {crumbs.map((crumb, index) => (
        <Fragment key={crumb.label}>
          {index > 0 && <span>•</span>}
          {crumb.to ? (
            <Link to={crumb.to}>{crumb.label}</Link>
          ) : (
            <span>{crumb.label}</span>
          )}
        </Fragment>
      ))}
    </div>
  );
}

export function Title({ children }: { children?: React.ReactNode }) {
  return <h1 className="text-title-md self-center leading-none">{children}</h1>;
}

type FilterOption = { active: boolean; label: string; to: string };

function NewsFilters({
  gender,
  section
}: {
  gender: NewsGender;
  section: NewsSection;
}) {
  const showDiscover = !section.nonGendered;
  const activeGender = section.nonGendered ? '' : gender.value;

  const categoryOptions: FilterOption[] = NEWS_SECTIONS.map((option) => ({
    active: option.value === section.value,
    label: option.label,
    to: buildNewsPath(option.nonGendered ? '' : activeGender, option.value)
  }));

  const genderOptions: FilterOption[] = NEWS_GENDERS.map((option) => ({
    active: option.value === gender.value,
    label: option.label,
    to: buildNewsPath(option.value, section.value)
  }));

  return (
    <>
      <div className="hidden flex-col items-center gap-3 self-center md:flex">
        <FilterBar options={categoryOptions} />
        {showDiscover ? <FilterBar options={genderOptions} /> : null}
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        <FiltersMobileDropdown
          activeLabel={section.label}
          header="Category"
          options={categoryOptions}
        />
        {showDiscover ? (
          <FiltersMobileDropdown
            activeLabel={gender.label}
            header="Discover"
            options={genderOptions}
          />
        ) : null}
      </div>
    </>
  );
}

function FilterBar({
  header,
  options
}: {
  header?: string;
  options: FilterOption[];
}) {
  return (
    <div className="flex items-center gap-5">
      {header && (
        <div className="text-sm tracking-wide text-light-grey">{header}:</div>
      )}
      <div className="flex gap-2">
        {options.map((option) => (
          <Filter
            key={option.to}
            active={option.active}
            text={option.label}
            to={option.to}
          />
        ))}
      </div>
    </div>
  );
}

function Filter({
  active,
  mobile,
  text,
  to
}: {
  active?: boolean;
  mobile?: boolean;
  text: string;
  to: string;
}) {
  return (
    <Link
      className={`rounded-full px-5 text-center text-sm ${
        mobile ? 'py-2' : 'py-[2.6px]'
      } ${
        active
          ? 'bg-black text-white'
          : 'border border-grey-divide text-light-grey'
      }`}
      to={to}
    >
      {text}
    </Link>
  );
}

function FiltersMobileDropdown({
  activeLabel,
  header,
  options
}: {
  activeLabel: string;
  header: string;
  options: FilterOption[];
}) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [activeLabel]);

  return (
    <div className="mx-auto w-full px-1">
      <div className="text-label mb-2 block">{header}</div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-full border border-grey-divide px-4 py-3 text-left text-lg font-medium"
      >
        {activeLabel}
        <span className={`ml-2 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronIcon width="16" height="16" stroke="black" />
        </span>
      </button>

      {isOpen && (
        <div className="mt-4 flex flex-col space-y-3">
          {options.map((option) => (
            <Filter
              key={option.to}
              active={option.active}
              mobile
              text={option.label}
              to={option.to}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeaturedArticles({ features }: { features: FeatureListItem[] }) {
  if (!features.length) return null;

  return (
    <>
      <div className="hidden md:block">
        <FeaturedArticlesDesktop features={features} />
      </div>
      <div className="md:hidden">
        <FeaturedArticlesMobileCarousel features={features} />
      </div>
    </>
  );
}

function FeaturedArticlesDesktop({
  features
}: {
  features: FeatureListItem[];
}) {
  return (
    <div className="mb-8 grid grid-cols-3 gap-8">
      <FeedArticleLink
        article={features[0]!}
        className="col-span-2 flex flex-col gap-2"
        moduleType={FEED_MODULE_TYPES.featured}
        position={1}
      >
        <Image
          className="aspect-[16/9] w-full rounded-lg object-cover"
          alt={features[0]!.image.alt}
          src={features[0]!.image.url}
          pad={false}
          loading="lazy"
          responsiveOptions={{
            lg: { width: 1024 },
            md: { width: 800 },
            sm: { width: 576 },
            xs: { width: 357 }
          }}
          progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
        />
        <div className="flex flex-col gap-3">
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight">
            {features[0]!.title}
          </h2>
          <p className="line-clamp-3 text-sm">{features[0]!.excerpt}</p>
        </div>
      </FeedArticleLink>
      <div className="flex flex-col gap-6">
        {features.slice(1, 3).map((feature, index) => (
          <FeedArticleLink
            article={feature}
            key={`${feature.title}-${index}`}
            moduleType={FEED_MODULE_TYPES.featured}
            position={index + 2}
          >
            <Image
              className="aspect-[16/9] w-full rounded-lg object-cover"
              alt={feature.image.alt}
              src={feature.image.url}
              pad={false}
              loading="lazy"
              responsiveOptions={{
                md: { width: 672 },
                xs: { width: 357 }
              }}
              progressiveLoadSettings={{
                highResQuality: 100,
                lowResQuality: 50
              }}
            />
            <p className="mt-2 line-clamp-3 font-heading text-lg font-semibold leading-normal">
              {feature.title}
            </p>
          </FeedArticleLink>
        ))}
      </div>
    </div>
  );
}

function FeaturedArticlesMobileCarousel({
  features
}: {
  features: FeatureListItem[];
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);

  const goToNextSlide = () => {
    setCurrentSlide((index) => (index + 1) % features.length);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((index) => (index + features.length - 1) % features.length);
  };

  const onTouchStart = (event: React.TouchEvent) => {
    startX.current = event.touches[0]!.clientX;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const endX = event.changedTouches[0]!.clientX;
    const deltaX = startX.current - endX;

    if (deltaX > 50) {
      goToNextSlide();
    } else if (deltaX < -50) {
      goToPrevSlide();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    const child = container?.children[currentSlide] as HTMLElement;

    if (!child || !container) return;

    const offset =
      child.offsetLeft - (container.offsetWidth - child.offsetWidth) / 2;

    container.scrollTo({ behavior: 'smooth', left: offset });
  }, [currentSlide]);

  return (
    <div className="flex flex-col gap-4">
      <div
        ref={containerRef}
        className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto px-1"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {features.map((feature, index) => (
          <FeedArticleLink
            article={feature}
            className="mr-4 w-full max-w-md shrink-0 snap-center"
            key={`${feature.title}-${index}`}
            moduleType={FEED_MODULE_TYPES.featured}
            position={index + 1}
          >
            <Image
              className="aspect-square w-full rounded-lg object-cover"
              alt={feature.image.alt}
              src={feature.image.url}
              pad={false}
              loading="lazy"
              responsiveOptions={{
                md: { width: 900 },
                xs: { width: 576 }
              }}
              progressiveLoadSettings={{
                highResQuality: 100,
                lowResQuality: 50
              }}
            />
            <p className="mt-2 line-clamp-3 font-heading text-lg font-semibold leading-normal">
              {feature.title}
            </p>
          </FeedArticleLink>
        ))}
      </div>

      <CarouselNavigation
        count={features.length}
        currentSlide={currentSlide}
        onClick={setCurrentSlide}
      />
    </div>
  );
}

function CarouselNavigation({
  count,
  currentSlide,
  onClick
}: {
  count: number;
  currentSlide: number;
  onClick: (index: number) => void;
}) {
  if (count < 2) return null;

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={`splash-nav-button-${index}`}
          className={`size-2 rounded-full ${currentSlide === index ? 'bg-black' : 'bg-grey-hide'}`}
          onClick={() => onClick(index)}
        />
      ))}
    </div>
  );
}

export function ArticleFeedList({
  features,
  moduleType,
  region
}: {
  features: FeatureListItem[];
  moduleType: string;
  region: Region;
}) {
  if (!features.length) return null;

  return (
    <>
      <hr />
      <div className="grid grid-cols-3 gap-8">
        {features.map((feature, index) => (
          <FeedArticleLink
            article={feature}
            className="col-span-3 flex flex-col gap-3 md:flex-row lg:col-span-2 lg:gap-5"
            key={`${feature.title}-${index}`}
            moduleType={moduleType}
            position={index + 1}
          >
            <Image
              className="aspect-[16/9] w-full rounded-md object-cover md:w-[370px]"
              alt={feature.image.alt}
              src={feature.image.url}
              pad={false}
              loading="lazy"
              responsiveOptions={{
                xs: { width: 576 }
              }}
              progressiveLoadSettings={{
                highResQuality: 100,
                lowResQuality: 50
              }}
            />
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h3 className="text-title-sm line-clamp-3">{feature.title}</h3>
                {feature.excerpt && (
                  <p className="line-clamp-3 hidden text-sm md:block">
                    {feature.excerpt}
                  </p>
                )}
              </div>
              <p className="flex gap-2 text-sm text-grey-hide">
                <span>
                  {formatArticleListItemData(feature.dates.published, region)}
                </span>
                {feature.author?.name ? (
                  <>
                    <span>•</span>
                    <span>{feature.author.name}</span>
                  </>
                ) : (
                  <></>
                )}
              </p>
            </div>
          </FeedArticleLink>
        ))}
      </div>
    </>
  );
}
